import { relative } from "@library/Utilities";
import dayjs, { Dayjs } from "dayjs";
import type { IVehicle } from "../IVehicle";
import type { ICondition, IConditionDto } from "./ICondition";
import type { IPosition } from "./IPosition";
import type { OdometerPosition } from "./OdometerPosition";

export class DatePosition implements IPosition
{

    next: Dayjs;
    last: Dayjs;
    current: Dayjs = dayjs();
    score: number = 0;
    label: string = '';
    relativeLabel: string = '';
    isIncomplete: boolean = false;
    isOverdue: boolean = false;
    isDue: boolean = false;
    isSpecificDate: boolean;

    constructor( model: IConditionDto, isSpecificDate: boolean )
    {
        this.next = dayjs( model.nextDate ?? '' );
        this.last = this.getLastDate( model.lastDate );
        this.isSpecificDate = isSpecificDate;
        this.initialise( model.score );
    }
    
    /**
     * Initialises the position once the next, current and last are set
     */
    private initialise( score?: number )
    {
        this.score = score ?? this.getScore();
        this.label = this.next.isValid() ? this.next.format( 'Do MMM \'YY' ) : '';
        this.relativeLabel = this.getRelativeLabel();
        this.isIncomplete = this.next.isValid() == false;
        this.isOverdue = this.isIncomplete == false && this.next.isBefore( this.current.add( 1, 'day' ), 'date' );
        this.isDue = this.isIncomplete == false && this.isOverdue == false && this.score > 0 && this.score < 0.2;
    }

    /**
     * Sets the parameters of the position
     * @param next 
     * @param last 
     */
    set( next: Dayjs, last?: Dayjs )
    {
        this.next = next;
        this.last = last ?? this.last;
        this.initialise();
    }

    /**
     * Sets the date from the odometer position when the date has no data
     * @param condition 
     * @param vehicle 
     * @param odometer 
     */
    setFromOdometer( condition: ICondition, vehicle: IVehicle, odometer: OdometerPosition )
    {

        const avgKmsPerDay = vehicle.estimatedAnnualKilometers / 365;
        const kmsToNext = odometer.next - odometer.current;
        const daysToNext = Math.max( kmsToNext / avgKmsPerDay, 1 );
        this.next = dayjs().add( daysToNext, 'day' );

        if( this.last.isValid() == false )
        {
            const kmsToLast = odometer.current - odometer.last;
            const daysToLast = Math.max( kmsToLast / avgKmsPerDay, 1 );
            this.last = dayjs().subtract( daysToLast, 'day' );
        }

        this.initialise();
        
    }

    /**
     * If we have a next date, but no last date, make the last date twelve months previous
     * @param lastDate 
     * @returns 
     */
    private getLastDate( lastDate: string | Dayjs | undefined )
    {
        const last = dayjs( lastDate ?? '' );
        if( this.next.isValid() && last.isValid() == false )
        {
            return this.next.subtract( 1, 'year' );
        }  
        return last;
    }

    /**
     * Calculates the score for the date condition
     * @returns 
     */
    private getScore()
    {

        if( this.next.isValid() == false ) return 1; // cant process it
        if( this.next.isBefore( dayjs() ) ) return 0; // overdue
        if( this.last.isValid() == false ) return 1; // cant process it
        if( this.next.isBefore( this.last ) ) return 1; // invalid

        const lastTime = this.last.toDate().getTime();
        const normalisedCurrent = dayjs().toDate().getTime() - lastTime; // current from last
        const normalisedNext = this.next.toDate().getTime() - lastTime; // next from last

        return Math.round( ( 1 - Math.min( Math.max( normalisedCurrent / normalisedNext, 0 ), 1 ) ) * 100 ) / 100;

    }

    /**
     * Gets the label for the date condition
     * @returns 
     */
    private getRelativeLabel()
    {
        if( this.next.isValid() == false ) return '';
        if( this.isSpecificDate ) return this.label;
        if( this.next.isSame( dayjs(), 'day' ) ) return 'today';
        if( this.next.isSame( dayjs().subtract( 1, 'day' ), 'day' ) ) return 'yesterday';
        if( this.next.isSame( dayjs().subtract( 1, 'day' ), 'week' ) ) return 'this week';
        if( this.next.isSame( dayjs().subtract( 7, 'day' ), 'week' ) ) return 'last week';
        if( this.next.isSame( dayjs().add( 1, 'day' ), 'day' ) ) return 'tomorrow';
        if( this.next.isSame( dayjs().add( 1, 'day' ), 'week' ) ) return 'this week';
        if( this.next.isSame( dayjs().add( 7, 'day' ), 'week' ) ) return 'next week';
        return relative( this.next );
    }

    /**
     * Gets the label as a relative date, using odometer values
     */
    public getLabelFromOdometer( currentOdometer: number, nextOdometer: number, averageKmsPerYear: number )
    {
        const distance = nextOdometer - currentOdometer;
        const ratio = distance / averageKmsPerYear;
        const days = ratio * 365;
        const nextDate = dayjs().add( days, 'day' );
        return relative( nextDate );
    }

}